import { COMPETENCY_LABELS } from '@/lib/competency'
import type { Competency, StarComponentKey, StoryDraft } from '@/types/interview'
import { useState } from 'react'
import { Link } from 'react-router-dom'

type FieldKey = 'situation' | StarComponentKey

const FIELDS: { key: FieldKey; label: string; hint: string }[] = [
  { key: 'situation', label: 'Situation', hint: 'What was going on, and why it mattered' },
  { key: 'task', label: 'Task', hint: 'What you were responsible for' },
  { key: 'action', label: 'Action', hint: 'What you specifically did' },
  { key: 'result', label: 'Result', hint: 'What changed — numbers help, but any outcome counts' },
]

interface StoryDraftReviewProps {
  draft: StoryDraft
  saving: boolean
  /** The free-tier cap blocked the save — show the upgrade beat instead of Save. */
  capHit: boolean
  onSave: (edited: StoryDraft) => void
  onKeepTalking: () => void
}

export function StoryDraftReview({
  draft,
  saving,
  capHit,
  onSave,
  onKeepTalking,
}: StoryDraftReviewProps) {
  const [title, setTitle] = useState(draft.title)
  const [competency, setCompetency] = useState<Competency | null>(draft.competency)
  const [roleTag, setRoleTag] = useState(draft.roleTag ?? '')
  const [text, setText] = useState<Record<FieldKey, string>>({
    situation: draft.situation,
    task: draft.task ?? '',
    action: draft.action ?? '',
    result: draft.result ?? '',
  })

  const canSave = title.trim().length > 0 && competency !== null && text.situation.trim().length > 0

  function buildEdited(): StoryDraft {
    const orNull = (v: string) => (v.trim() ? v.trim() : null)
    const task = orNull(text.task)
    const action = orNull(text.action)
    const result = orNull(text.result)
    return {
      title: title.trim(),
      competency,
      situation: text.situation.trim(),
      task,
      action,
      result,
      roleTag: orNull(roleTag),
      missingComponents: (['task', 'action', 'result'] as const).filter(
        (k) => ({ task, action, result })[k] === null
      ),
    }
  }

  return (
    <section className="space-y-4 rounded-xl border border-primary/30 bg-primary/5 p-4">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold">Here's your story — check it over</h2>
        <p className="text-xs text-muted-foreground">
          This is a first draft built only from what you told me. Fix anything that's off and fill
          in what's missing before you save it.
        </p>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="draft-title"
          className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
        >
          Title
        </label>
        <input
          id="draft-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Competency
          </span>
          <select
            value={competency ?? ''}
            onChange={(e) => setCompetency((e.target.value || null) as Competency | null)}
            aria-label="Competency"
            className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Pick the best fit</option>
            {(Object.keys(COMPETENCY_LABELS) as Competency[]).map((key) => (
              <option key={key} value={key}>
                {COMPETENCY_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label
            htmlFor="draft-role-tag"
            className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
          >
            Context (optional)
          </label>
          <input
            id="draft-role-tag"
            value={roleTag}
            onChange={(e) => setRoleTag(e.target.value)}
            maxLength={80}
            placeholder="e.g. Club treasurer"
            className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {FIELDS.map(({ key, label, hint }) => {
        const missing = draft.missingComponents.includes(key as StarComponentKey)
        return (
          <div key={key} className="space-y-1">
            <label
              htmlFor={`draft-${key}`}
              className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
            >
              {label}
            </label>
            <textarea
              id={`draft-${key}`}
              value={text[key]}
              onChange={(e) => setText((prev) => ({ ...prev, [key]: e.target.value }))}
              rows={2}
              maxLength={2000}
              placeholder={missing ? `We didn't hear this part — ${hint.toLowerCase()}` : hint}
              className="w-full resize-none rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
            />
            {missing && !text[key].trim() && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400">
                Not mentioned in the conversation — add it if you can, or leave it blank.
              </p>
            )}
          </div>
        )
      })}

      {capHit && (
        <div
          role="alert"
          className="space-y-2 rounded-lg border border-primary/40 bg-background p-3 text-sm"
        >
          <p className="font-semibold">Your free story slots are full.</p>
          <p className="text-xs text-muted-foreground">
            Upgrade to Pro to keep this one — and every story you build from here on.
          </p>
          <Link
            to="/settings"
            className="inline-block rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
          >
            Upgrade to Pro
          </Link>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
        <button
          type="button"
          onClick={onKeepTalking}
          disabled={saving}
          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-40"
        >
          Discard & keep talking
        </button>
        {!capHit && (
          <button
            type="button"
            onClick={() => onSave(buildEdited())}
            disabled={!canSave || saving}
            className="rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save to my Story Bank'}
          </button>
        )}
      </div>
    </section>
  )
}
